import { BrandAndProduct, Entity, formatTicker } from '@api';
import { Icon } from '@design';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { NonLinearStepper } from '@terminal/components/non-linear-stepper/NonLinearStepper';
import { GeoSourceEvidenceSection } from '@terminal/widgets/company-to-geo-widget/tabs/components/source-evidence-section/GeoSourceEvidenceSection';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import React from 'react';
import { useTranslation } from 'react-i18next';

import styles from './BrandAndProductDrawerContent.module.scss';

export interface BrandAndProductDrawerContentProps {
  entity: Entity;
  logoUrl: string;
  productAndBrand: BrandAndProduct;
  title?: string;
}

export const BrandAndProductDrawerContent = ({
  entity,
  logoUrl,
  productAndBrand,
  title,
}: BrandAndProductDrawerContentProps) => {
  const { t } = useTranslation(['widget']);

  return (
    <div className={styles.root}>
      <div className={styles.sectionRow} data-testid="product-stepper-section">
        {title && <div className={styles.titleMedium}>{title}</div>}
        <div className={styles.stepperWrapper}>
          <NonLinearStepper
            activeIndex={0}
            steps={[
              {
                logoUrl: logoUrl,
                hoverLabel: formatTicker(entity),
                connectorLabel: t('widget:assetOverview.knowledgeGraph.offers'),
                isSimple: false,
                highlightConnector: true,
              },
              {
                imgNode: (
                  <Icon
                    className={styles.styledIcon}
                    iconName="Award"
                    size={18}
                  />
                ),
                iconColor: 'var(--kg-brand)',
                hoverLabel: '',
                highlightConnector: true,
              },
            ]}
            iconSize={23}
          />
        </div>
        <div className={styles.bodySmall}>
          {productAndBrand.relationship.description}
        </div>
      </div>
      <div className={styles.sectionRow}>
        <div className={styles.titleBase}>
          {t(
            'widget:assetOverview.knowledgeGraph.brandsAndProducts.brandAndProductRank'
          )}
        </div>
        <div className={styles.styledRankWrapper}>
          <div className={styles.rankContainer}>
            <div className={styles.rankLabel}>
              {t(
                'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.current'
              )}
            </div>
            <div className={styles.rankFlexRow}>
              <TopRankIcon rank={productAndBrand.relationship.rank} />
            </div>
          </div>
        </div>
      </div>
      <ErrorBoundaryWrapper>
        <GeoSourceEvidenceSection
          evidence={productAndBrand.relationship.evidence ?? []}
          sourceEvidence={productAndBrand.relationship.source_evidence ?? null}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
